import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { WishlistHandles } from '@theme/wishlist-handles';

/**
 * A custom element that displays the wishlist icon's live item-count badge.
 *
 * Mirrors `<cart-icon>` (`assets/cart-icon.js`) so the two badges behave and look
 * identical, but reads `@theme/wishlist-handles` (localStorage) instead of the cart.
 * That class is a placeholder data source until a wishlist app is installed; see
 * docs/implemented-features.md, "Wishlist heart".
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble - The wishlist bubble element.
 * @property {HTMLElement} wishlistBubbleText - The wishlist bubble text element.
 * @property {HTMLElement} wishlistBubbleCount - The wishlist bubble count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(WishlistHandles.changeEventName, this.onWishlistChange);
    window.addEventListener('storage', this.onStorageChange);
    this.renderWishlistBubble(WishlistHandles.getCount(), false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(WishlistHandles.changeEventName, this.onWishlistChange);
    window.removeEventListener('storage', this.onStorageChange);
  }

  /**
   * Handles the same-tab wishlist change event dispatched by `WishlistHandles`.
   * @param {CustomEvent<{count: number}>} event
   */
  onWishlistChange = (event) => {
    const count = event.detail?.count ?? WishlistHandles.getCount();

    this.renderWishlistBubble(count);
  };

  /**
   * Handles the cross-tab `storage` event, so a wishlist change in another tab
   * updates this badge too. `event.key` is `null` when `localStorage.clear()` runs.
   * @param {StorageEvent} event
   */
  onStorageChange = (event) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== WishlistHandles.storageKey) return;

    this.renderWishlistBubble(WishlistHandles.getCount());
  };

  /**
   * Renders the wishlist bubble. Mirrors `CartIcon#renderCartBubble` so the two
   * badges share one visual language, including the grow animation on change.
   * @param {number} count - The number of wishlisted products.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderWishlistBubble = async (count, animate = true) => {
    this.refs.wishlistBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);
    this.refs.wishlistBubbleCount.textContent = count < 100 ? String(count) : '';

    this.classList.toggle('header-actions__wishlist-badge--has-items', count > 0);

    if (!animate || count === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.wishlistBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.wishlistBubbleText);

    this.refs.wishlistBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
